import * as tus from "tus-js-client";
import { apiClient } from "@/lib/api/axios";
import { fetchAllPaged } from "@/lib/api/paged";
import type {
  Property,
  PublicProperty,
  PropertyImage,
  PropertyVideo,
  ListParams,
  PropertyShareLink,
  PropertyPartnership,
  PartnerIdentifierKind,
  BrokerLookupResult,
  PropertyLookupResult,
  ReceivedPartnership,
  PropertyAccessAuthorization,
  PropertyAvailabilityCheck,
  OwnerWelcome,
  SendOwnerWelcomeInput,
  PropertySign,
  CreatePropertySignInput,
  ExternalPartnerInvite,
  GenerateExternalPartnerInviteInput,
  CoBrokingClient,
  PropertyTransaction,
  ContractClosureKind,
  ClosureRequestedBy,
  PropertyReturnAnswer,
  PropertyReturnAnswerSource,
  PropertyReturnCheck,
  PropertyReturnStatus,
  ShareAnalytics,
  ShareVisitsPage,
} from "../mocks/fixtures";
import { toPublicProperty } from "../mocks/fixtures";
import type { PropertyStatus } from "../schemas/property";

interface UploadUrlResponse {
  mediaAssetId: string;
  uploadUrl: string;
  method: string;
  headers: Record<string, string>;
  expiresAt: string;
}

/** Foto como o crm-api devolve (capa = position 0). */
interface ServerPropertyImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  position: number;
  isCover: boolean;
  caption?: string;
}

/** Normaliza a foto do servidor (`position`) para o shape do front (`order`). */
function mapServerImage(img: ServerPropertyImage): PropertyImage {
  return {
    id: img.id,
    url: img.url,
    thumbnailUrl: img.thumbnailUrl,
    isCover: img.isCover,
    order: img.position,
    caption: img.caption,
  };
}

/** Vídeo como o crm-api devolve — hospedado no Vimeo (ordem = position). hub#636. */
interface ServerPropertyVideo {
  id: string;
  propertyId?: string;
  vimeoId: string;
  status: "uploading" | "processing" | "ready" | "error";
  position: number;
  caption?: string | null;
  embedUrl?: string | null;
  thumbnailUrl?: string | null;
  durationSeconds?: number | null;
}

/** Normaliza o vídeo do servidor (`position`) para o shape do front (`order`). */
function mapServerVideo(v: ServerPropertyVideo): PropertyVideo {
  return {
    id: v.id,
    vimeoId: v.vimeoId,
    status: v.status,
    order: v.position,
    caption: v.caption ?? undefined,
    embedUrl: v.embedUrl ?? undefined,
    thumbnailUrl: v.thumbnailUrl ?? undefined,
    durationSeconds: v.durationSeconds ?? undefined,
  };
}

/** Normaliza fotos e vídeos de um imóvel vindo do servidor. */
function mapProperty(p: Property): Property {
  const next: Property = { ...p };
  const images = p.images as unknown as ServerPropertyImage[] | undefined;
  if (Array.isArray(images)) next.images = images.map(mapServerImage);
  const videos = p.videos as unknown as ServerPropertyVideo[] | undefined;
  if (Array.isArray(videos)) next.videos = videos.map(mapServerVideo);
  return next;
}

/** Fotos e vídeos viajam pelo fluxo presigned, nunca no JSON do imóvel — remove se vierem. */
function stripMedia<T extends { images?: unknown; videos?: unknown }>(
  input: T,
): Omit<T, "images" | "videos"> {
  const rest = { ...input };
  delete (rest as { images?: unknown }).images;
  delete (rest as { videos?: unknown }).videos;
  return rest;
}

export function listProperties(params?: ListParams): Promise<Property[]> {
  return fetchAllPaged<Property>("/properties", params ?? {}).then((items) => items.map(mapProperty));
}

export async function getProperty(id: string): Promise<Property> {
  const { data } = await apiClient.get<Property>(`/properties/${id}`);
  return mapProperty(data);
}

/** Métricas do link público do imóvel (`GET /v1/properties/{id}/share-analytics`). hub#577. */
export async function getShareAnalytics(id: string): Promise<ShareAnalytics> {
  const { data } = await apiClient.get<ShareAnalytics>(`/properties/${id}/share-analytics`);
  return data;
}

/** Página de acessos do link público (cursor, mais recentes primeiro). hub#577. */
export async function getShareVisitsPage(
  id: string,
  cursor?: string,
  limit?: number,
): Promise<ShareVisitsPage> {
  const { data } = await apiClient.get<ShareVisitsPage>(
    `/properties/${id}/share-analytics/visits`,
    { params: { cursor: cursor || undefined, limit } },
  );
  return data;
}

/** Foto do histórico (camelCase do crm-api). `amount` vem como número. hub#483. */
interface ServerPropertyTransaction {
  id: string;
  kind: "sale" | "rent";
  status: "active" | "closed" | "canceled";
  amount: number;
  partyName: string;
  brokerName: string;
  startDate: string;
  endDate?: string | null;
  dealId: string;
  dealCode: string;
  contractId?: string | null;
  contractCode?: string | null;
  closureKind?: ContractClosureKind | null;
  closureRequestedBy?: ClosureRequestedBy | null;
  keysReturnedOn?: string | null;
  closureNotes?: string | null;
  penaltyAmount?: number | null;
  exitInspectionId?: string | null;
  returnCheckId?: string | null;
  returnCheckStatus?: PropertyReturnStatus | null;
  returnAnswer?: PropertyReturnAnswer | null;
  returnAnswerSource?: PropertyReturnAnswerSource | null;
  returnAnsweredAt?: string | null;
  returnExpectedAvailableOn?: string | null;
}

/** Histórico de vendas e locações do imóvel (negócios + contratos). hub#483. */
export async function getPropertyTransactions(id: string): Promise<PropertyTransaction[]> {
  const { data } = await apiClient.get<ServerPropertyTransaction[]>(`/properties/${id}/transactions`);
  return data.map((t) => ({
    id: t.id,
    kind: t.kind,
    status: t.status,
    amount: t.amount,
    partyName: t.partyName,
    brokerName: t.brokerName,
    startDate: t.startDate,
    endDate: t.endDate ?? undefined,
    dealId: t.dealId,
    dealCode: t.dealCode,
    contractId: t.contractId ?? undefined,
    contractCode: t.contractCode ?? undefined,
    closureKind: t.closureKind ?? undefined,
    closureRequestedBy: t.closureRequestedBy ?? undefined,
    keysReturnedOn: t.keysReturnedOn ?? undefined,
    closureNotes: t.closureNotes ?? undefined,
    penaltyAmount: t.penaltyAmount ?? undefined,
    exitInspectionId: t.exitInspectionId ?? undefined,
    returnCheckId: t.returnCheckId ?? undefined,
    returnCheckStatus: t.returnCheckStatus ?? undefined,
    returnAnswer: t.returnAnswer ?? undefined,
    returnAnswerSource: t.returnAnswerSource ?? undefined,
    returnAnsweredAt: t.returnAnsweredAt ?? undefined,
    returnExpectedAvailableOn: t.returnExpectedAvailableOn ?? undefined,
  }));
}

/**
 * Consultas ao proprietário abertas por encerramento de contrato (hub#760).
 * A pendente é a que vira o cartão "aguardando decisão do dono" na ficha.
 */
export async function listPropertyReturnChecks(
  propertyId: string,
): Promise<PropertyReturnCheck[]> {
  const { data } = await apiClient.get<PropertyReturnCheck[]>(
    `/properties/${propertyId}/return-checks`,
  );
  return data;
}

/**
 * Registra a resposta que o corretor ouviu por fora (telefone/WhatsApp). A consulta
 * continua obrigatória — muda só quem preencheu, e o histórico marca isso. hub#760.
 * Idempotency-Key: o duplo-clique não pode registrar a decisão do dono duas vezes.
 */
export async function answerPropertyReturnCheck(
  propertyId: string,
  checkId: string,
  input: { answer: PropertyReturnAnswer; message?: string; expectedAvailableOn?: string },
): Promise<PropertyReturnCheck> {
  const { data } = await apiClient.post<PropertyReturnCheck>(
    `/properties/${propertyId}/return-checks/${checkId}/answer`,
    {
      answer: input.answer,
      message: input.message || undefined,
      expectedAvailableOn: input.expectedAvailableOn || undefined,
    },
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}

/** Reenvia o link da consulta pendente ao proprietário (mesmo token). hub#760. */
export async function resendPropertyReturnCheck(
  propertyId: string,
  checkId: string,
): Promise<PropertyReturnCheck> {
  const { data } = await apiClient.post<PropertyReturnCheck>(
    `/properties/${propertyId}/return-checks/${checkId}/resend`,
    null,
  );
  return data;
}

/** Devolve um imóvel vendido/alugado ao book de ofertas (sem contrato a encerrar). hub#484. */
export async function reopenProperty(id: string): Promise<Property> {
  const { data } = await apiClient.post<Property>(`/properties/${id}/reopen`, null);
  return mapProperty(data);
}

/** Tom/tamanho do assistente de descrição (hub#505). */
export type EnhanceTone = "sober" | "selling" | "luxury";
export type EnhanceLength = "short" | "full";

/**
 * Melhora a descrição do imóvel via IA (hub#505) — chama o crm-api, que ancora nos dados reais
 * do imóvel. Não persiste: devolve o texto sugerido p/ o corretor revisar. Sempre real (sem mock).
 */
export async function enhancePropertyDescription(
  id: string,
  input: { tone?: EnhanceTone; length?: EnhanceLength; baseText?: string },
): Promise<{ description: string }> {
  const { data } = await apiClient.post<{ description: string }>(
    `/properties/${id}/description/enhance`,
    input,
  );
  return data;
}

// ── Interesse no imóvel → lead + demanda + lembrete (hub#520) ──
export type InterestIntent = "buy" | "rent" | "both";

export interface RegisterInterestInput {
  personId: string;
  intent: InterestIntent;
  priority?: "low" | "medium" | "high";
  createDemand?: boolean;
  followUpInDays?: number;
  notes?: string;
}

export interface RegisterInterestResult {
  lead: { id: string; name: string; propertyId?: string | null; intent: string; priority: string };
  demandId?: string | null;
}

/**
 * Marca o interesse de um contato num imóvel do book (cenário A, hub#520): o crm-api cria o lead,
 * a demanda opcional e o lembrete de follow-up. Sempre real (ação de domínio).
 */
export async function registerPropertyInterest(
  propertyId: string,
  input: RegisterInterestInput,
): Promise<RegisterInterestResult> {
  const { data } = await apiClient.post<RegisterInterestResult>(
    `/properties/${propertyId}/interest`,
    input,
  );
  return data;
}

/**
 * Vitrine pública (anônima). O backend já anula owner/comissão/parcerias no DTO
 * público; ainda assim projetamos para `PublicProperty` (toPublicProperty) como
 * defesa em profundidade — campos privados nunca atravessam para a UI pública.
 * Auditoria P2-public-property-fe-type.
 */
export async function getPropertyByCode(code: string): Promise<PublicProperty> {
  const { data } = await apiClient.get<Property>(`/public/properties/${code}`);
  return toPublicProperty(mapProperty(data));
}

/** Busca global do CRM por código público IMV- (resolve só imóvel do próprio corretor). hub#129. */
export async function lookupProperty(code: string): Promise<PropertyLookupResult> {
  const { data } = await apiClient.get<PropertyLookupResult>("/properties/lookup", {
    params: { code },
  });
  return data;
}

export async function createProperty(
  input: Omit<Property, "id" | "internalCode" | "publicSlug" | "shareCode" | "createdAt" | "updatedAt">,
) {
  const { data } = await apiClient.post<Property>("/properties", stripMedia(input), {
    headers: { "Idempotency-Key": crypto.randomUUID() },
  });
  return mapProperty(data);
}

export async function updateProperty(
  id: string,
  input: Partial<
    Omit<Property, "id" | "internalCode" | "publicSlug" | "shareCode" | "createdAt" | "updatedAt">
  >,
): Promise<Property> {
  const { data } = await apiClient.put<Property>(`/properties/${id}`, stripMedia(input));
  return mapProperty(data);
}

export async function deleteProperty(id: string): Promise<{ ok: true }> {
  await apiClient.delete(`/properties/${id}`);
  return { ok: true };
}

/**
 * Upload de foto em 3 passos (padrão presigned do crm-api, igual a inspections):
 * 1) reserva URL assinada + MediaAsset; 2) sobe o binário direto no storage (sem
 * o interceptor de auth do app); 3) anexa a foto ao imóvel referenciando a mídia.
 */
/**
 * Passos 1-2 do fluxo presigned: reserva a URL assinada + MediaAsset e sobe o
 * binário direto no storage (sem o interceptor de auth do app). Devolve o
 * `mediaAssetId` para quem precisar anexá-lo (foto do imóvel, foto de preview…).
 */
async function uploadMediaAsset(file: Blob, purpose: string, nameHint: string): Promise<string> {
  const contentType = file.type || "image/jpeg";
  const ext = contentType.split("/")[1] ?? "jpg";
  const fileName = `${nameHint}.${ext}`;

  const { data: upload } = await apiClient.post<UploadUrlResponse>("/media/upload-url", {
    purpose,
    contentType,
    fileName,
    sizeBytes: file.size,
  });

  await fetch(upload.uploadUrl, {
    method: upload.method || "PUT",
    body: file,
    headers: upload.headers,
  });

  return upload.mediaAssetId;
}

export async function uploadPropertyImage(
  propertyId: string,
  file: Blob,
  position: number,
): Promise<PropertyImage> {
  const mediaAssetId = await uploadMediaAsset(
    file,
    "property_image",
    `property-${Date.now()}-${position}`,
  );

  const { data } = await apiClient.post<ServerPropertyImage>(
    `/properties/${propertyId}/images`,
    {
      mediaAssetId,
      position,
      caption: null,
    },
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return mapServerImage(data);
}

/**
 * Foto de preview do WhatsApp (hub#577). Reutiliza o MESMO fluxo presigned das
 * fotos do imóvel para obter um `mediaAssetId` avulso (sem anexar à galeria).
 */
export async function uploadPreviewImageAsset(file: Blob): Promise<string> {
  return uploadMediaAsset(file, "property_image", `property-preview-${Date.now()}`);
}

/** Define a foto dedicada de preview do link público (og:image). hub#577. */
export async function setPreviewImage(id: string, mediaAssetId: string): Promise<Property> {
  const { data } = await apiClient.put<Property>(`/properties/${id}/preview-image`, {
    mediaAssetId,
  });
  return mapProperty(data);
}

/** Remove a foto de preview (volta a usar a capa da galeria como og:image). hub#577. */
export async function clearPreviewImage(id: string): Promise<Property> {
  const { data } = await apiClient.delete<Property>(`/properties/${id}/preview-image`);
  return mapProperty(data);
}

export async function deletePropertyImage(propertyId: string, imageId: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/images/${imageId}`);
}

/** Reordena as fotos (índice 0 = capa). A lista precisa conter todas as fotos atuais. */
export async function reorderPropertyImages(propertyId: string, imageIds: string[]): Promise<void> {
  await apiClient.put(`/properties/${propertyId}/images/reorder`, { imageIds });
}

/** Resposta do ticket de upload: o vídeo criado + o endpoint tus pré-autorizado pelo Vimeo. hub#636. */
interface VideoUploadTicketResponse {
  video: ServerPropertyVideo;
  uploadLink: string;
  expiresAt: string | null;
}

/**
 * Sobe o arquivo direto ao Vimeo via protocolo tus (endpoint já criado/autorizado
 * pelo crm-api — sem token do Vimeo no cliente). Resolve quando o upload conclui.
 */
function tusUpload(
  uploadLink: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      // O endpoint já existe (o crm-api criou o vídeo no Vimeo); reanexamos a ele.
      uploadUrl: uploadLink,
      retryDelays: [0, 1000, 3000, 5000],
      metadata: { filename: file.name, filetype: file.type || "video/mp4" },
      onError: (error) => reject(error),
      onProgress: (sent, total) => {
        if (onProgress && total > 0) onProgress(Math.round((sent / total) * 100));
      },
      onSuccess: () => resolve(),
    });
    upload.start();
  });
}

/**
 * Vídeo curto do imóvel via Vimeo (hub#636): 1) POST upload-ticket cria o vídeo no
 * Vimeo e devolve o endpoint tus pré-autorizado; 2) sobe os bytes direto ao Vimeo
 * via tus (sem token no cliente). O vídeo volta em `processing`; o status vira
 * `ready` depois — a tela de detalhe faz polling via `getPropertyVideoStatus`.
 */
export async function createVideoUploadTicket(
  propertyId: string,
  file: File,
  caption?: string,
  onProgress?: (percent: number) => void,
): Promise<PropertyVideo> {
  const { data } = await apiClient.post<VideoUploadTicketResponse>(
    `/properties/${propertyId}/videos/upload-ticket`,
    {
      fileName: file.name,
      sizeBytes: file.size,
      caption: caption?.trim() || undefined,
    },
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );

  await tusUpload(data.uploadLink, file, onProgress);

  return mapServerVideo(data.video);
}

/** Estado atual do vídeo no Vimeo (polling enquanto não estiver `ready`). hub#636. */
export async function getPropertyVideoStatus(
  propertyId: string,
  videoId: string,
): Promise<PropertyVideo> {
  const { data } = await apiClient.get<ServerPropertyVideo>(
    `/properties/${propertyId}/videos/${videoId}/status`,
  );
  return mapServerVideo(data);
}

export async function deletePropertyVideo(propertyId: string, videoId: string): Promise<void> {
  await apiClient.delete(`/properties/${propertyId}/videos/${videoId}`);
}

/** Reordena os vídeos (índice 0 = primeiro). A lista precisa conter todos os vídeos atuais. */
export async function reorderPropertyVideos(propertyId: string, videoIds: string[]): Promise<void> {
  await apiClient.put(`/properties/${propertyId}/videos/reorder`, { videoIds });
}

export async function changePropertyStatus(id: string, status: PropertyStatus): Promise<Property> {
  const { data } = await apiClient.patch<Property>(`/properties/${id}/status`, { status });
  return data;
}

export async function createShareLink(
  propertyId: string,
  input: {
    showAddress: boolean;
    includeVideos?: boolean;
    note?: string;
    expiresInDays?: number;
    maxViews?: number;
    /**
     * Gera o link SEM a identidade do corretor (hub#765). Exige os dados de quem pediu —
     * o servidor devolve 422 `property_share_token.requester_required` sem eles.
     */
    hideBrokerIdentity?: boolean;
    requesterName?: string | null;
    requesterEmail?: string | null;
    requesterPhone?: string | null;
  },
): Promise<PropertyShareLink> {
  const { data } = await apiClient.post<PropertyShareLink>(
    `/properties/${propertyId}/share-tokens`,
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}

export async function revokeShareLink(_propertyId: string, linkId: string): Promise<Property> {
  await apiClient.delete(`/share-tokens/${linkId}`);
  const { data } = await apiClient.get<Property>(`/properties/${_propertyId}`);
  return data;
}

/**
 * Exclui o link do histórico. Revogar mata o link e mantém a linha marcada como
 * revogada; excluir tira da lista.
 */
export async function deleteShareLink(propertyId: string, linkId: string): Promise<Property> {
  await apiClient.delete(`/share-tokens/${linkId}/permanently`);
  const { data } = await apiClient.get<Property>(`/properties/${propertyId}`);
  return data;
}

export async function generateExternalPartnerInvite(
  propertyId: string,
  input: GenerateExternalPartnerInviteInput = {},
): Promise<ExternalPartnerInvite> {
  const { data } = await apiClient.post<ExternalPartnerInvite>(
    "/proposals/external-partner-invites",
    {
      propertyId,
      name: input.name?.trim() || undefined,
      email: input.email?.trim() || undefined,
      phone: input.phone?.trim() || undefined,
      isWhatsApp: Boolean(input.isWhatsApp),
      sendEmail: Boolean(input.sendEmail),
    },
  );
  return data;
}

export async function listExternalPartnerInvites(propertyId?: string): Promise<ExternalPartnerInvite[]> {
  const { data } = await apiClient.get<ExternalPartnerInvite[]>(
    "/proposals/external-partner-invites",
    { params: propertyId ? { propertyId } : undefined },
  );
  return data;
}

export async function cancelExternalPartnerInvite(inviteId: string): Promise<{ ok: true }> {
  await apiClient.delete(`/proposals/external-partner-invites/${inviteId}`);
  return { ok: true };
}

export async function indicateCoBrokingClient(
  shareId: string,
  input: { personId: string; role?: string },
): Promise<CoBrokingClient> {
  const { data } = await apiClient.post<CoBrokingClient>(`/partnerships/${shareId}/client`, input);
  return data;
}

export async function listCoBrokingClients(shareId: string): Promise<CoBrokingClient[]> {
  const { data } = await apiClient.get<CoBrokingClient[]>(`/partnerships/${shareId}/clients`);
  return data;
}

export async function revokeCoBrokingClient(
  shareId: string,
  linkId: string,
): Promise<{ ok: true }> {
  await apiClient.delete(`/partnerships/${shareId}/client/${linkId}`);
  return { ok: true };
}

// ADR-0042: importar o cliente indicado para a base da captação foi REMOVIDO
// (POST /v1/co-broking-clients/{linkId}/import agora responde 410 Gone). Na "caixa"
// mediada, B usa o próprio Person — nada é materializado na base de A.

export async function createPartnership(
  propertyId: string,
  input: {
    identifierKind: PartnerIdentifierKind;
    identifier: string;
    partnerPercent: number;
    note?: string;
  },
): Promise<PropertyPartnership> {
  const { data } = await apiClient.post<PropertyPartnership>(
    `/properties/${propertyId}/partnerships`,
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}

export async function revokePartnership(
  propertyId: string,
  partnershipId: string,
): Promise<Property> {
  await apiClient.delete(`/partnerships/${partnershipId}`);
  const { data } = await apiClient.get<Property>(`/properties/${propertyId}`);
  return data;
}

/**
 * Exclui a co-corretagem do histórico — inclusive a ATIVA, que o servidor encerra antes
 * de apagar. Devolve 409 quando a parceria já gerou proposta.
 */
export async function deletePartnership(
  propertyId: string,
  partnershipId: string,
): Promise<Property> {
  await apiClient.delete(`/partnerships/${partnershipId}/permanently`);
  const { data } = await apiClient.get<Property>(`/properties/${propertyId}`);
  return data;
}

export async function lookupBroker(params: {
  creci?: string;
  uf?: string;
  code?: string;
}): Promise<BrokerLookupResult> {
  const { data } = await apiClient.get<BrokerLookupResult>("/brokers/lookup", { params });
  return data;
}

export async function acceptPartnership(
  propertyId: string,
  partnershipId: string,
): Promise<Property> {
  await apiClient.post(`/partnerships/${partnershipId}/accept`);
  const { data } = await apiClient.get<Property>(`/properties/${propertyId}`);
  return data;
}

export async function declinePartnership(
  propertyId: string,
  partnershipId: string,
): Promise<Property> {
  await apiClient.post(`/partnerships/${partnershipId}/decline`);
  const { data } = await apiClient.get<Property>(`/properties/${propertyId}`);
  return data;
}

/**
 * Convites de co-corretagem recebidos pelo corretor logado (caixa do convidado).
 * O servidor já filtra pelo broker do JWT; default = pendentes (`invited`).
 * As respostas (accept/decline) NÃO re-buscam a propriedade: ela é de outro
 * corretor e não está no book deste (a lista é reinvalidada na UI).
 */
export async function listReceivedPartnerships(status?: string): Promise<ReceivedPartnership[]> {
  const { data } = await apiClient.get<ReceivedPartnership[]>("/partnerships/received", {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function acceptReceivedPartnership(partnershipId: string): Promise<void> {
  await apiClient.post(`/partnerships/${partnershipId}/accept`);
}

export async function declineReceivedPartnership(partnershipId: string): Promise<void> {
  await apiClient.post(`/partnerships/${partnershipId}/decline`);
}

export async function createAccessAuthorization(
  propertyId: string,
  input: {
    scheduledAt: string;
    durationMinutes?: number;
    visitors: { nome: string; documento?: string; telefone?: string }[];
    ownerPhone: string;
    note?: string;
    requestedByName?: string;
  },
): Promise<PropertyAccessAuthorization> {
  const { data } = await apiClient.post<PropertyAccessAuthorization>(
    `/properties/${propertyId}/access-authorizations`,
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}

export async function cancelAccessAuthorization(
  propertyId: string,
  authId: string,
): Promise<Property> {
  await apiClient.delete(`/properties/${propertyId}/access-authorizations/${authId}`);
  const { data } = await apiClient.get<Property>(`/properties/${propertyId}`);
  return data;
}

export async function getAccessAuthorizationByToken(token: string) {
  const { data } = await apiClient.get<{
    property: Pick<Property, "id" | "title" | "internalCode" | "address" | "ownerName"> & {
      coverImageUrl?: string;
    };
    authorization: PropertyAccessAuthorization;
  } | null>(`/public/access-authorizations/${token}`);
  return data;
}

export async function respondAccessAuthorization(
  token: string,
  response: "authorized" | "denied",
  note?: string,
): Promise<PropertyAccessAuthorization> {
  const { data } = await apiClient.post<PropertyAccessAuthorization>(
    `/public/access-authorizations/${token}/respond`,
    { response, note },
  );
  return data;
}

/** Histórico de check-ins de disponibilidade do imóvel (mais recentes primeiro no back). hub#585. */
export async function listAvailabilityChecks(
  propertyId: string,
): Promise<PropertyAvailabilityCheck[]> {
  const { data } = await apiClient.get<PropertyAvailabilityCheck[]>(
    `/properties/${propertyId}/availability-checks`,
  );
  return data;
}

/**
 * Dispara (ou reenvia) a carta de boas-vindas ao proprietário. hub#707.
 * Idempotency-Key OBRIGATÓRIA no backend: o duplo-clique não pode virar dois
 * e-mails na caixa do dono. Sem `resend`, um envio ainda vivo volta com
 * `alreadySent: true` e nada é reenviado.
 */
export async function sendOwnerWelcome(
  propertyId: string,
  input: SendOwnerWelcomeInput,
): Promise<OwnerWelcome> {
  const { data } = await apiClient.post<OwnerWelcome>(
    `/properties/${propertyId}/owner-welcome`,
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}

/** Último envio de boas-vindas do imóvel; null quando nunca foi enviado (204). hub#707. */
export async function getOwnerWelcome(propertyId: string): Promise<OwnerWelcome | null> {
  const res = await apiClient.get<OwnerWelcome | "">(`/properties/${propertyId}/owner-welcome`);
  return res.status === 204 || !res.data ? null : res.data;
}

// ── Gerador de Placa (hub#709) ────────────────────────────────────────────

/**
 * Enfileira a geração de uma placa. Responde 202 com o pedido em `queued` — o
 * PDF é composto no Worker (QuestPDF + Ghostscript), então a tela acompanha pelo
 * `getPropertySign` até `ready`.
 *
 * Idempotency-Key OBRIGATÓRIA: sem o header o backend responde 400 ANTES do
 * serviço, e todo bloqueio legítimo (409) viraria "requisição inválida".
 */
export async function requestPropertySign(
  propertyId: string,
  input: CreatePropertySignInput,
): Promise<PropertySign> {
  const { data } = await apiClient.post<PropertySign>(
    `/properties/${propertyId}/sign`,
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}

/** Placas já pedidas para o imóvel (mais recentes primeiro). Sem URL assinada. hub#709. */
export async function listPropertySigns(propertyId: string): Promise<PropertySign[]> {
  const { data } = await apiClient.get<PropertySign[]>(`/properties/${propertyId}/signs`);
  return data;
}

/** Estado do pedido, com as URLs assinadas do PDF e da prévia quando `ready`. hub#709. */
export async function getPropertySign(
  propertyId: string,
  requestId: string,
): Promise<PropertySign> {
  const { data } = await apiClient.get<PropertySign>(
    `/properties/${propertyId}/sign/${requestId}`,
  );
  return data;
}

/**
 * Liga/desliga o link impresso de uma placa (spec §9.0). Desativar NÃO mata o QR:
 * ele passa a levar ao cartão do corretor.
 */
export async function setPropertySignLink(
  propertyId: string,
  requestId: string,
  enabled: boolean,
): Promise<PropertySign> {
  const { data } = await apiClient.put<PropertySign>(
    `/properties/${propertyId}/sign/${requestId}/link`,
    { enabled },
  );
  return data;
}

/** Reativa o imóvel (volta o status para Active) após pausa/encerramento por terceiros. hub#585. */
export async function reactivateProperty(id: string): Promise<Property> {
  const { data } = await apiClient.post<Property>(`/properties/${id}/reactivate`, null);
  return mapProperty(data);
}

// ── Oferta do imóvel à imobiliária (ADR-0039 revisado, hub#706) ────────────

/**
 * Estado da oferta. `daysWithout`-style: `linkedOrganizationId: null` = corretor solo, e a tela
 * nem mostra o controle. `canWithdraw: false` = já há negócio/contrato, e a casa virou parte no
 * histórico.
 */
export interface PropertyOffer {
  offered: boolean;
  organizationId: string | null;
  canWithdraw: boolean;
  linkedOrganizationId: string | null;
}

export async function getPropertyOffer(propertyId: string): Promise<PropertyOffer> {
  const { data } = await apiClient.get<PropertyOffer>(`/properties/${propertyId}/offer-to-agency`);
  return data;
}

export async function offerPropertyToAgency(propertyId: string): Promise<PropertyOffer> {
  const { data } = await apiClient.post<PropertyOffer>(
    `/properties/${propertyId}/offer-to-agency`,
    undefined,
    // Idempotência (hub#706): retry de rede não gera oferta duplicada nem 409 espúrio.
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}

export async function withdrawPropertyOffer(propertyId: string): Promise<PropertyOffer> {
  const { data } = await apiClient.delete<PropertyOffer>(
    `/properties/${propertyId}/offer-to-agency`,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return data;
}
